// 8-point grid system
export const SPACING = {
  one: 4,
  two: 8,
  twoHalf: 12,
  three: 16,
  four: 24,
  five: 32,
  six: 48,
  seven: 64,
  eight: 72,
  nine: 80,
} as const;

// Screen widths
export const WIDTHS = {
  S: 980,
  SM: 1441,
  M: 1280,
  // L: 1728,
} as const;

/**
 * Corner radius, named by ROLE — the scale DESIGN.md specifies.
 *
 * The ordinal names below are deprecated. They are the reason radius drifted:
 * `BORDER_RADIUS.four` tells you nothing about when to reach for it, so the
 * choice was arbitrary at every call site and neighbouring components ended up
 * 2px apart. An audit found 62% of the 343 radius declarations in `src/`
 * bypassing this object entirely, pills written three different ways across 58
 * sites, and `max` used exactly zero times.
 *
 * Values are unitless — every call site appends its own `px`.
 */
export const BORDER_RADIUS = {
  /** Buttons, inputs, tags, small controls. */
  sm: 4,
  /** Cards, panels, list rows, tab bars. */
  md: 8,
  /** Modals and large containers. */
  lg: 12,
  /** Pills and avatars. Prefer `50%` for true circles — a different concept. */
  full: 9999,

  /**
   * @deprecated Ordinal names carry no meaning. Use `sm`/`md`/`lg`/`full`.
   *
   * Kept as aliases rather than removed: 123 call sites across 119 files still
   * use them, and this repo has 63 open branches — renaming them in one commit
   * would conflict with nearly all of it. `six`/`ten` (6px and 10px) are not
   * sanctioned by DESIGN.md and exist only to keep those sites compiling; they
   * are what let neighbouring components differ by 2px, and they go away as the
   * migration proceeds.
   */
  one: 4,
  /** @deprecated 6px is off the design scale — use `sm` (4) or `md` (8). */
  two: 6,
  /** @deprecated Use `md`. */
  three: 8,
  /** @deprecated 10px is off the design scale — use `md` (8) or `lg` (12). */
  four: 10,
  /** @deprecated Use `lg`. */
  five: 12,
  /** @deprecated Use `full`. 100px is not reliably a pill: it under-rounds
   * anything taller than 200px. */
  max: 100,
} as const;

// Might be worth including browser height into the mix
export const COMPONENTS = {
  header: {
    main: 56,
    sub: 48,
    buttons: 40,
  },
  toggleTabs: 32,
  marketSelectionDropdown: 271,
  footer: {
    desktop: 32,
    // Account + Nav
    mobileSubNav: 56,
    mobileNav: 56,
    mobileFooterTotal: 56 + 64,
  },
  portfolioNavigation: 40,
  portfolioFilter: 48,
  mobileTvChart: 360,
  mobileCustomChart: 320,
  banner: 24,
  notificationPanel: 343,
  tradeTicketOrderbookHeight: 420,
  emptyContent: 236,
  tableRow: 40,
  portfolioTableRow: 56,
  portfolioRewardsTableRow: 48,
  tableRowSmall: 40,
  perpsTableRowSmall: 24,
  tableHeader: 32,
  portfolioTableHeader: 48,
  tableActionButtonsCellWidth: 112,
  selectHeight: 32,
  chart: {
    tradingViewSpotPrice: 290,
    spotPrice: 48,
    dateFilter: 48,
    portfolio: 240,
  },
  overview: {
    section: 288,
  },
  slideOutModalContentMaxWidth: 400,
  modalHeaderHeight: 56,
  tabs: 56,
  secondaryTabs: 48,
  pageTabsHeight: 40,
  tradeTicketTab: 56,
  dateSelection: 64,
  tradeModalFilterHeight: 437,
  showHideTradeModalButtonSize: 64,
  subHeaderButtonSize: 56,
  accountModalRowHeight: 48,
  tradeModalMinHeight: 890,
  tradeSectionWidth: 304,
  perpsOrderbookWidth: 304,
  perpsOrderbookSpreadRowHeight: 24,
  // Floor for the chart + orderbook row. The bottom bar below it used to be a
  // rigid height, so every pixel lost to a shorter window came out of the
  // chart (215px at a 739px viewport). The chart holds this and the bottom
  // bar gives way instead, down to its own header height.
  tradeTopSectionMinHeight: 390,
  tradeBottomBar: {
    header: 48,
    height: {
      large: 320,
      small: 200,
    },
  },
  mobileTradeBottomBarMinHeight: 300,
  portfolioSideTab: 320,
  modalButton: 48,
  cmdKModalFooter: 48,
  dateSelectionHeight: 32,
  optionsSpotChartWrapperWidth: 160,
  mobilePerpPositionBarHeight: 80,
  paginationHeight: 64,
  strikeHeader: 40,
  announcementHeight: 32,
  mobileTabBar: 56,
  slider: 40,
} as const;
